import { ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Toolbar, AppBar, IconButton,
  Menu, MenuItem, Stack, Avatar, useTheme, Button,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../contexts/AuthContext';

const navItems = [
  { label: 'Patients', icon: <PeopleIcon />, path: '/' },
  { label: 'Presets', icon: <SettingsIcon />, path: '/presets' },
];

interface Props {
  children: ReactNode;
}

export default function Shell({ children }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isGuest, signOut } = useAuth();
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();

  const userInitial = user?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || '?';

  return (
    <Box sx={{ minHeight: '100vh', background: theme.palette.background.default, pb: location.pathname === '/' ? 0 : 10 }}>
      {isGuest && (
        <Box sx={{
          background: 'linear-gradient(90deg, #5c4d96, #5aaa8a)',
          color: '#fff', px: 2, py: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap',
        }}>
          <Typography variant="body2" fontWeight={600}>
            You're using a guest account — create a free account to save your data permanently.
          </Typography>
          <Button
            size="small" variant="outlined"
            onClick={() => navigate('/login')}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Create account
          </Button>
        </Box>
      )}
      {children}

      <Menu anchorEl={userAnchor} open={Boolean(userAnchor)} onClose={() => setUserAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {isGuest ? (
          <MenuItem onClick={() => { setUserAnchor(null); navigate('/login'); }}>
            Create account
          </MenuItem>
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </MenuItem>
        )}
        <MenuItem onClick={() => { setUserAnchor(null); signOut(); }}>
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> {isGuest ? 'Exit guest session' : 'Sign Out'}
        </MenuItem>
      </Menu>

      {location.pathname !== '/' && <AppBar position="fixed" elevation={0} sx={{
        top: 'auto', bottom: 0,
        borderRadius: '24px 24px 0 0',
        mx: 'auto',
        left: 0,
        right: 0,
      }}>
        <Toolbar sx={{ justifyContent: 'space-around', py: 0.5 }}>
          {navItems.map(item => {
            const active = location.pathname === item.path;
            return (
              <IconButton
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  width: 52, height: 52,
                  borderRadius: '50%',
                  bgcolor: active ? 'rgba(30,58,95,0.08)' : 'transparent',
                  color: active ? 'primary.main' : theme.palette.text.secondary,
                  border: active ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                  transition: 'all 0.2s',
                }}
              >
                {item.icon}
              </IconButton>
            );
          })}
          <IconButton
            onClick={e => setUserAnchor(e.currentTarget)}
            sx={{
              width: 52, height: 52,
              borderRadius: '50%',
              border: '2px solid transparent',
            }}
          >
            <Avatar
              src={user?.photoURL || undefined}
              sx={{ width: 32, height: 32, fontSize: 14, bgcolor: isGuest ? '#9e9e9e' : '#3bb8b8', color: '#fff' }}
            >
              {isGuest ? '?' : userInitial}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>}
    </Box>
  );
}
